import { CollectionProductResponse, CollectionResponse } from 'api/product';
import { getSmartCollection } from 'api/product/getSmartCollection';
import {
  SmartCollectionInput,
  SmartCollectionResponse,
} from 'api/product/getSmartCollection/types';
import { isSmartCollectionResponse } from 'api/product/getSmartCollection/utils';
import { useConfig } from 'config';
import {
  CatNutritionSegmentationIds,
  DogAgeSegmentationIds,
  SegmentationData,
} from 'constants/segmentation';
import { useNinetailedAbTestContext } from 'contexts/ninetailed-ab-test';
import { useSegmentationContext } from 'contexts/segmentation';
import { CollectionProduct } from 'models/collection/collection';
import { NO_REDIRECT_TO_CDP } from 'modules/pet-profile/constants/rules';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { getPetProfileUrlWithType } from 'utils/pet-profile/get-pet-profile-url';
import { State } from '../../state/interface';

interface Output {
  ctaLink?: string;
  products: CollectionProduct[];
  isLoading: boolean;
  title?: string;
}

const DEFAULT_PRODUCTS_IDS = [543559974946, 1589124071458, 1685503606818];

/**
 * Hook to handle the different scenarios of the need capture component.
 * The hook will return the products to be displayed in the component.
 * Based on the input, the hook will fetch the products from the static collection API or the dynamic collection API.
 */
const useNeedCaptureScenarios = (
  petType: 'cat' | 'dog',
  state: State
): Output => {
  const {
    selectedAge,
    selectedBreed,
    selectedNeeds,
    selectedAllergies,
    selectedFoodTypes,
    selectedTastePreferences,
    selectedWetFoodTypes,
  } = state || {};
  const [shouldUseDefaultProducts, setShouldUseDefaultProducts] =
    useState<boolean>(false);
  const [isWaitingForAudience, setIsWaitingForAudience] = useState(true);
  const [ctaLink, setCtaLink] = useState<string>();
  const { lastSegmentation } = useSegmentationContext();
  const { experimentHeaderStr } = useNinetailedAbTestContext();
  const { locale } = useRouter();
  const { shopId } = useConfig();

  const findHandle = (segmentation: string): string | undefined => {
    // Since the ImmuneSystem and HairballHelp don't have dedicated CDPs, we redirect to the pet-profile page
    if (
      segmentation === CatNutritionSegmentationIds.ImmuneSystem ||
      segmentation === CatNutritionSegmentationIds.HairballHelp
    ) {
      return '/pet-profile?type=cat';
    }

    return SegmentationData.segmentation.find(
      (segment) => segment.id === segmentation
    )?.redirectPages[0];
  };

  const cleanUrl = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1];
  };

  const selectedAgeOnly =
    selectedAge &&
    !selectedBreed &&
    (typeof selectedNeeds === 'undefined' || selectedNeeds?.length === 0);
  const selectedBreedOnly =
    selectedBreed &&
    !selectedAge &&
    (typeof selectedNeeds === 'undefined' || selectedNeeds?.length === 0);
  const selectedNeedsOnly =
    selectedNeeds &&
    selectedNeeds?.length > 0 &&
    !selectedAge &&
    !selectedBreed;

  useEffect(() => {
    if (selectedAgeOnly) {
      const link = findHandle(selectedAge);
      setShouldUseDefaultProducts(
        selectedAge === DogAgeSegmentationIds.DogAdult
      );
      setCtaLink(link);
      return;
    }

    if (selectedBreedOnly) {
      const link = findHandle(selectedBreed);
      setShouldUseDefaultProducts(false);
      // For mix breed, user should be sent to pet-profile page
      if (NO_REDIRECT_TO_CDP.includes(selectedBreed)) {
        setCtaLink(getPetProfileUrlWithType(petType));
      } else {
        setCtaLink(link);
        return;
      }
    }

    if (selectedNeedsOnly) {
      const link =
        selectedNeeds.length > 1
          ? getPetProfileUrlWithType(petType)
          : findHandle(selectedNeeds[0]);
      setShouldUseDefaultProducts(false);
      setCtaLink(link);
      return;
    }

    setShouldUseDefaultProducts(false);
    setCtaLink(getPetProfileUrlWithType(petType));

    return () => {
      setShouldUseDefaultProducts(false);
      setCtaLink(undefined);
    };
  }, [
    state,
    selectedAge,
    selectedAgeOnly,
    selectedAllergies,
    selectedBreed,
    selectedBreedOnly,
    selectedNeeds,
    selectedNeedsOnly,
    petType,
  ]);

  const payload: SmartCollectionInput | undefined = useMemo(() => {
    if (!selectedAge && !selectedBreed && selectedNeeds?.length === 0)
      return undefined;

    let url: string | undefined;

    if (selectedAgeOnly) {
      url = cleanUrl(findHandle(selectedAge));
    }

    if (selectedBreedOnly) {
      url = cleanUrl(findHandle(selectedBreed));
    }

    if (selectedNeedsOnly) {
      url = cleanUrl(findHandle(selectedNeeds[0]));
    }

    return {
      age: selectedAge,
      breed: selectedBreed,
      needs: selectedNeeds,
      allergies: selectedAllergies,
      wet_food_tags: selectedWetFoodTypes,
      protein_tags: selectedTastePreferences,
      food_types: selectedFoodTypes,
      slug: url,
      pet_type: petType,
      ninetailed: experimentHeaderStr.split(','),
    };
  }, [
    selectedAge,
    selectedBreed,
    selectedNeeds,
    selectedAgeOnly,
    selectedBreedOnly,
    selectedNeedsOnly,
    selectedAllergies,
    selectedWetFoodTypes,
    selectedTastePreferences,
    selectedFoodTypes,
    petType,
    experimentHeaderStr,
  ]);

  // Check if the experiment has the required audience
  // Unfortunatelly, the experimentHeaderStr is updated progressively, so we need to check if the experimentHeaderStr includes the audience
  useEffect(() => {
    if (experimentHeaderStr.includes('5DFTkfeIFDQeE9pyEe0rs3')) {
      setIsWaitingForAudience(false);
    }
  }, [experimentHeaderStr]);

  const { data: collection, isValidating } = useSWR<
    SmartCollectionResponse | CollectionResponse
  >(payload && !isWaitingForAudience ? [payload, locale] : null, ([payload]) =>
    getSmartCollection(payload, shopId)
  );

  const collectionProducts = useMemo(() => {
    if (!collection) return null;

    let filteredProducts: CollectionResponse['products'] = [];

    if (!isSmartCollectionResponse(collection)) {
      if (shouldUseDefaultProducts) {
        filteredProducts = collection.products.filter((product) =>
          DEFAULT_PRODUCTS_IDS.includes(product.id)
        );
      } else {
        filteredProducts = collection.products.filter(
          (product) =>
            lastSegmentation &&
            product.segmentation &&
            product.segmentation[lastSegmentation]?.isBestSeller
        );
      }
    }

    const productsToRender = isSmartCollectionResponse(collection)
      ? collection.items
      : filteredProducts;

    const formattedProducts = productsToRender.map(
      (
        product:
          | CollectionProductResponse
          | SmartCollectionResponse['items'][number]
      ) => new CollectionProduct(product)
    );

    return {
      products: formattedProducts.slice(0, 3),
    };
  }, [collection, lastSegmentation, shouldUseDefaultProducts]);

  const title = useMemo(() => {
    if (isSmartCollectionResponse(collection)) {
      return collection.title;
    }
  }, [collection]);

  return {
    products: collectionProducts?.products || [],
    ctaLink,
    isLoading: isValidating || isWaitingForAudience,
    title,
  };
};

export default useNeedCaptureScenarios;
